var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import { useForm, Controller, FormProvider, } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField, SelectionControlLabel, Checkbox, LoadingButton, } from '@one-thd/sui-atomic-components';
import { addressesApi } from '../../../api/common/addresses';
import { ERR_UNABLE_TO_SAVE } from '../../../constants/response-error-messages';
import { Alert } from '../../shared/alert/alert';
import { mapAddAddressToRequestModel, mapAddressFormToMappedModal, mapEditAddressToRequestModel, } from '../../../utils/mapping-addresses';
import { getTextFieldStatus, getAddressFormValues } from '../../../utils/form';
import { PHONE_PLACEHOLDER } from '../../../constants/placeholders';
import { useAddresses } from '../../../hooks/use-addresses';
import { FormTitle } from '../../shared/form-title/form-title';
import { FORM_MODE, } from '../../../models/common/addresses';
import { validationSchema } from './validation';
import { getHyphenatedPhoneNumber } from '../../../utils/general';
import { TextButton } from '../../shared/text-button/text-button';
import { DefaultAddressModal } from '../default-address-modal/default-address-modal';
import { useModal } from '../../../hooks/use-modal';
import { setAddPhoneLinkAnalytics, setAddEditAddressAnalytics, } from '../../../utils/address-analytics';
import { pushErrorEvent } from '../../../utils/analytics';
import { AddressFields } from '../../shared/address/address-fields';
export const AddressForm = ({ onAddSuccess, onEditSuccess, }) => {
    var _a;
    const { isB2B, addresses, addressFormMode, editableAddress, setAddressFormMode, setEditableAddress, changeDefault, } = useAddresses();
    const { openModal, hideModal, setComponentProps } = useModal();
    const isAddAddressForm = addressFormMode === FORM_MODE.ADD;
    const formTitleText = isAddAddressForm ? 'Add Address' : 'Edit Address';
    const defaultValues = getAddressFormValues(editableAddress);
    const [isAlternatePhoneEnabled, setAlternatePhoneField] = useState(!!defaultValues.alternatePhone || false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema),
        mode: 'onTouched',
    });
    const { control, formState: { errors: formErrors, isValid, dirtyFields }, handleSubmit, getValues, } = formMethods;
    const { isZipCodePending, isZipCodeApiError } = getValues();
    const isNoAddresses = !Number((_a = addresses === null || addresses === void 0 ? void 0 : addresses.paginationInfo) === null || _a === void 0 ? void 0 : _a.totalNumberOfRecords);
    const resetAddressForm = () => {
        setAddressFormMode(null);
        setEditableAddress(null);
    };
    const handleOnClickAlternatePhone = () => {
        setAlternatePhoneField(true);
        setAddPhoneLinkAnalytics(isAddAddressForm);
    };
    const isFormDirty = () => Object.values(dirtyFields).some((value) => {
        return value || false;
    });
    const executeUpdateAddress = (updateAddressRequest, editableAdr, isDefault) => {
        const handleSuccessEditResponse = () => {
            onEditSuccess(editableAdr.addrIdentifier);
            setAddEditAddressAnalytics(isAddAddressForm, updateAddressRequest === null || updateAddressRequest === void 0 ? void 0 : updateAddressRequest.isDefault);
        };
        const handleErrorEditResponse = () => {
            setError(ERR_UNABLE_TO_SAVE);
            if (isDefault) {
                pushErrorEvent('address card: set as default', 'inline', 'n/a', ERR_UNABLE_TO_SAVE.message);
                return;
            }
            pushErrorEvent('address card: edit address', 'inline', 'n/a', ERR_UNABLE_TO_SAVE.message);
        };
        if (!isB2B && isDefault && !defaultValues.isDefault) {
            return changeDefault(null, updateAddressRequest)
                .then(() => {
                handleSuccessEditResponse();
            })
                .catch(() => {
                handleErrorEditResponse();
            });
        }
        return addressesApi
            .editAddress(isB2B, updateAddressRequest)
            .then(() => {
            handleSuccessEditResponse();
        })
            .catch(() => {
            handleErrorEditResponse();
        });
    };
    const executeAddAddress = (requestData, isDefault) => {
        return addressesApi
            .addNewAddress(isB2B, requestData)
            .then(({ data }) => __awaiter(void 0, void 0, void 0, function* () {
            if (!isB2B && isDefault && !isNoAddresses) {
                const newAdr = data.addresses.address[0];
                yield changeDefault(null, newAdr);
            }
            setAddEditAddressAnalytics(isAddAddressForm, isDefault);
            onAddSuccess();
        }))
            .catch(() => {
            setError(ERR_UNABLE_TO_SAVE);
            pushErrorEvent('address card: add address', 'inline', 'n/a', ERR_UNABLE_TO_SAVE.message);
        });
    };
    const onSubmitForm = (values) => {
        setError(null);
        if (!isFormDirty()) {
            resetAddressForm();
            return;
        }
        const isFormValid = !isZipCodePending || !isZipCodeApiError || isValid;
        if (isFormValid) {
            if (isAddAddressForm) {
                const requestData = mapAddAddressToRequestModel(isB2B, values, isNoAddresses);
                if ((values === null || values === void 0 ? void 0 : values.isDefault) && !isNoAddresses) {
                    const mappedFormValues = mapAddressFormToMappedModal(values);
                    openModal(DefaultAddressModal, {
                        address: mappedFormValues,
                        onSave: () => {
                            setComponentProps({ isLoading: true });
                            executeAddAddress(requestData, true).finally(() => {
                                setComponentProps({ isLoading: false });
                                hideModal();
                            });
                        },
                        isLoading: false,
                    });
                }
                else {
                    setLoading(true);
                    executeAddAddress(requestData, values.isDefault).finally(() => {
                        setLoading(false);
                    });
                }
            }
            if (!isAddAddressForm && addresses && editableAddress) {
                const addressDataFromResponse = addresses.response.find((adr) => adr.addrIdentifier === (editableAddress === null || editableAddress === void 0 ? void 0 : editableAddress.addrIdentifier));
                if (!addressDataFromResponse) {
                    throw Error('Editable address not found in addresses list');
                }
                const updateAddressRequest = mapEditAddressToRequestModel(isB2B, values, addressDataFromResponse);
                if ((values === null || values === void 0 ? void 0 : values.isDefault) && !defaultValues.isDefault) {
                    const mappedFormValues = mapAddressFormToMappedModal(values);
                    openModal(DefaultAddressModal, {
                        address: mappedFormValues,
                        onSave: () => __awaiter(void 0, void 0, void 0, function* () {
                            setComponentProps({ isLoading: true });
                            yield executeUpdateAddress(updateAddressRequest, editableAddress, values.isDefault).finally(() => {
                                hideModal();
                            });
                        }),
                        isLoading: false,
                    });
                }
                else {
                    setLoading(true);
                    executeUpdateAddress(updateAddressRequest, editableAddress, values.isDefault).finally(() => {
                        setLoading(false);
                    });
                }
            }
        }
    };
    return (React.createElement("div", { className: "lg:sui-max-w-[437px]" },
        React.createElement(FormTitle, { title: formTitleText, actionLabel: "Cancel", onActionClick: resetAddressForm }),
        React.createElement(FormProvider, Object.assign({}, formMethods),
            React.createElement("form", { onSubmit: handleSubmit(onSubmitForm), className: "sui-pt-8" },
                React.createElement("div", { className: "sui-grid sui-gap-y-5 sui-mb-4" },
                    React.createElement(AddressFields, null,
                        isB2B && (React.createElement(Controller, { name: "nickName", control: control, render: ({ field }) => {
                                var _a;
                                const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                return (React.createElement(TextField, { label: "Address Nickname", status: getTextFieldStatus(formErrors.nickName), statusMessage: (_a = formErrors.nickName) === null || _a === void 0 ? void 0 : _a.message, optional: true, value: value || '', name: name, onChange: onChange, InputProps: {
                                        inputProps: Object.assign(Object.assign({}, rest), { 'data-qm-mask': '', 'data-testid': 'addressNicknameField' }),
                                    } }));
                            } })),
                        isB2B && (React.createElement(Controller, { name: "companyName", control: control, render: ({ field }) => {
                                var _a;
                                const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                return (React.createElement(TextField, { label: "Company Name", status: getTextFieldStatus(formErrors.companyName), statusMessage: (_a = formErrors.companyName) === null || _a === void 0 ? void 0 : _a.message, optional: true, value: value || '', name: name, onChange: onChange, InputProps: {
                                        inputProps: Object.assign(Object.assign({}, rest), { 'data-testid': 'company-name-add-address' }),
                                    } }));
                            } }))),
                    isAlternatePhoneEnabled && (React.createElement(Controller, { name: "alternatePhone", control: control, render: ({ field }) => {
                            var _a;
                            const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                            return (React.createElement(TextField, { label: "Alternate Phone", optional: true, status: getTextFieldStatus(formErrors.alternatePhone), statusMessage: (_a = formErrors.alternatePhone) === null || _a === void 0 ? void 0 : _a.message, placeholder: PHONE_PLACEHOLDER, value: value || '', name: name, onChange: (event) => {
                                    onChange(getHyphenatedPhoneNumber(event.currentTarget.value || ''));
                                }, InputProps: {
                                    inputProps: Object.assign(Object.assign({}, rest), { 'data-qm-mask': '', 'data-testid': 'alternate-phone-add-address' }),
                                } }));
                        } }))),
                !isAlternatePhoneEnabled && (React.createElement(TextButton, { className: "sui-py-4", onClick: handleOnClickAlternatePhone, dataTestId: "add-another-phone-add-address" }, "Add Another Phone")),
                React.createElement("div", { className: "sui-mb-8 sui-pt-4" },
                    React.createElement(SelectionControlLabel, { label: "Make this my default address" },
                        React.createElement(Controller, { name: "isDefault", control: control, render: ({ field }) => {
                                const { onChange, onBlur, name, ref } = field;
                                return (React.createElement(Checkbox, { onChange: onChange, onBlur: onBlur, name: name, ref: ref, checked: isNoAddresses || field.value, disabled: isNoAddresses || !!(editableAddress === null || editableAddress === void 0 ? void 0 : editableAddress.isDefault), "data-testid": "make-address-default-checkbox" }));
                            } }))),
                error && (React.createElement("div", { className: "sui-mb-8" },
                    React.createElement(Alert, { status: "error" }, error.message))),
                React.createElement("div", { className: "sui-grid sui-grid-cols-1 sui-gap-4 sm:sui-grid-cols-2" },
                    React.createElement("div", { className: "sui-order-last sm:sui-order-first" },
                        React.createElement(Button, { fullWidth: true, variant: "secondary", onClick: resetAddressForm, "data-testid": "cancel-add-address-btn" }, "Cancel")),
                    React.createElement(LoadingButton, { loading: loading, fullWidth: true, variant: "primary", type: "submit", "data-testid": "save-changes-add-address-btn" }, "Save Changes"))))));
};
